import React from 'react';
import { Star, CheckCircle } from 'lucide-react';

/**
 * ReviewTimelineCard — Timeline card for event completion + review state.
 * Three states:
 * 1. "Event Completed" + review_pending → violet card with completion notice + "Leave Review" CTA
 * 2. "Event Completed" + completed → green completion confirmation
 * 3. "Review Submitted" → green confirmation card
 */
const ReviewTimelineCard = ({ milestone, onLeaveReview }) => {
  if (!milestone) return null;

  const { title, detail, status, timestamp, data } = milestone;
  const isSubmitted = title === 'Review Submitted';
  const isCompletedDone = title === 'Event Completed' && status === 'completed';

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
        ', ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  // Green confirmation cards
  if (isSubmitted || isCompletedDone) {
    return (
      <div data-testid={isSubmitted ? 'review-card-submitted' : 'event-completed-card'} className="mx-6 my-1.5 flex items-start gap-2.5 px-3 py-2 rounded-lg border bg-emerald-50 border-emerald-200">
        <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-semibold text-emerald-800">{title}</span>
            <span className="text-[10px] text-slate-400 shrink-0">{formatDate(timestamp)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Violet card: Event Completed with Leave Review CTA
  return (
    <div data-testid="review-card-pending" className="mx-6 my-1.5 rounded-lg border border-violet-200 bg-violet-50/80 overflow-hidden">
      <div className="flex items-start gap-2.5 px-3 py-2">
        <Star className="w-3.5 h-3.5 mt-0.5 shrink-0 text-violet-600" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-semibold text-violet-800">{title}</span>
            <span className="text-[10px] text-slate-400 shrink-0">{formatDate(timestamp)}</span>
          </div>
          {detail && <p className="text-[11px] text-violet-600 mt-0.5">{detail}</p>}
        </div>
      </div>
      {onLeaveReview && data?.inquiry_id && (
        <button
          data-testid="review-leave-btn"
          onClick={() => onLeaveReview(data.inquiry_id)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-violet-500 hover:bg-violet-600 transition-colors border-t border-violet-200"
        >
          <Star className="w-3.5 h-3.5" />
          Leave Review
        </button>
      )}
    </div>
  );
};

export default ReviewTimelineCard;
